import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { 
  ArrowLeft, RefreshCcw, Trophy, BookOpen, FileText, Video, 
  CheckCircle, Clock, Award, Target, TrendingUp, Calendar,
  Star, Zap, Medal, Crown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StudentProgress() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [progressData, setProgressData] = useState(null);

  const token = localStorage.getItem('studentToken');

  useEffect(() => {
    if (!token) {
      navigate('/student/login');
      return;
    }
    fetchProgress();
  }, [token, navigate]);

  const fetchProgress = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/student-portal/lms/progress/detailed`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProgressData(data);
      } else {
        toast.error('Failed to load progress data');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load progress');
    } finally {
      setIsLoading(false);
    }
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getGrade = (percentage) => {
    if (percentage >= 90) return { grade: 'A+', color: 'text-green-600' };
    if (percentage >= 80) return { grade: 'A', color: 'text-green-500' };
    if (percentage >= 70) return { grade: 'B', color: 'text-blue-500' };
    if (percentage >= 60) return { grade: 'C', color: 'text-yellow-500' };
    if (percentage >= 50) return { grade: 'D', color: 'text-orange-500' };
    return { grade: 'F', color: 'text-red-500' };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <RefreshCcw className="animate-spin h-10 w-10 text-indigo-600" />
      </div>
    );
  }

  if (!progressData) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">No progress data available</p>
        <Button onClick={() => navigate('/student/dashboard')} className="mt-4">
          Go to Dashboard
        </Button>
      </div>
    );
  }

  const { content, assignments, quizzes, online_classes, weekly_activity, achievements } = progressData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/student/dashboard')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <TrendingUp className="text-indigo-600" />
              My Progress
            </h1>
            <p className="text-sm text-gray-500">{progressData.course_name}</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchProgress}>
          <RefreshCcw size={14} className="mr-1" /> Refresh
        </Button>
      </div>

      {/* Overall Progress Card */}
      <Card className="mb-6 bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-0" data-testid="overall-progress">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-indigo-100 text-sm">Overall Progress</p>
              <p className="text-5xl font-bold mt-1">{progressData.overall_progress}%</p>
              <p className="text-indigo-200 text-sm mt-2">Keep going, {progressData.student_name}! 🎯</p>
            </div>
            <div className="w-32 h-32 relative">
              <svg className="w-full h-full transform -rotate-90">
                <circle cx="64" cy="64" r="56" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="12" />
                <circle 
                  cx="64" cy="64" r="56" fill="none" stroke="white" strokeWidth="12"
                  strokeDasharray={`${progressData.overall_progress * 3.52} 352`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <Trophy className="w-10 h-10 text-yellow-300" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Progress Cards Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {/* Content Progress */}
        <Card className="hover:shadow-lg transition-shadow" data-testid="content-progress">
          <CardContent className="p-4 text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <BookOpen className="text-blue-600" size={24} />
            </div>
            <p className="text-2xl font-bold text-gray-900">{content.percentage}%</p>
            <p className="text-xs text-gray-500">Content Completed</p>
            <p className="text-xs text-blue-600 mt-1">{content.completed}/{content.total} items</p>
          </CardContent>
        </Card>

        {/* Assignment Progress */}
        <Card className="hover:shadow-lg transition-shadow" data-testid="assignment-progress">
          <CardContent className="p-4 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <FileText className="text-green-600" size={24} />
            </div>
            <p className="text-2xl font-bold text-gray-900">{assignments.percentage}%</p>
            <p className="text-xs text-gray-500">Assignments Done</p>
            <p className="text-xs text-green-600 mt-1">{assignments.submitted}/{assignments.total} submitted</p>
          </CardContent>
        </Card>

        {/* Quiz Progress */}
        <Card className="hover:shadow-lg transition-shadow" data-testid="quiz-progress">
          <CardContent className="p-4 text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <Target className="text-purple-600" size={24} />
            </div>
            <p className="text-2xl font-bold text-gray-900">{quizzes.percentage}%</p>
            <p className="text-xs text-gray-500">Quizzes Attempted</p>
            <p className="text-xs text-purple-600 mt-1">{quizzes.passed}/{quizzes.attempted} passed</p>
          </CardContent>
        </Card>

        {/* Class Attendance */}
        <Card className="hover:shadow-lg transition-shadow" data-testid="class-attendance">
          <CardContent className="p-4 text-center">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <Video className="text-orange-600" size={24} />
            </div>
            <p className="text-2xl font-bold text-gray-900">{online_classes.percentage}%</p>
            <p className="text-xs text-gray-500">Class Attendance</p>
            <p className="text-xs text-orange-600 mt-1">{online_classes.attended}/{online_classes.total} attended</p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Sections */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Assignment Details */}
        <Card data-testid="assignment-details">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="text-green-600" size={20} />
              Assignment Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <span className="text-sm text-gray-600">Average Score</span>
                <span className={`text-xl font-bold ${getGrade(assignments.average_percentage).color}`}>
                  {assignments.average_percentage}% ({getGrade(assignments.average_percentage).grade})
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 bg-gray-50 rounded">
                  <p className="text-lg font-bold text-gray-900">{assignments.total}</p>
                  <p className="text-xs text-gray-500">Total</p>
                </div>
                <div className="p-2 bg-blue-50 rounded">
                  <p className="text-lg font-bold text-blue-600">{assignments.submitted}</p>
                  <p className="text-xs text-gray-500">Submitted</p>
                </div>
                <div className="p-2 bg-green-50 rounded">
                  <p className="text-lg font-bold text-green-600">{assignments.graded}</p>
                  <p className="text-xs text-gray-500">Graded</p>
                </div>
              </div>
              {assignments.details && assignments.details.length > 0 && (
                <div className="mt-3 max-h-40 overflow-y-auto">
                  {assignments.details.slice(0, 5).map((a, i) => (
                    <div key={i} className="flex justify-between items-center py-2 border-b last:border-0">
                      <span className="text-sm truncate max-w-[150px]">{a.title}</span>
                      <span className={`text-sm font-medium ${a.status === 'graded' ? 'text-green-600' : a.status === 'submitted' ? 'text-blue-600' : 'text-gray-400'}`}>
                        {a.marks_obtained !== null ? `${a.marks_obtained}/${a.total_marks}` : a.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quiz Details */}
        <Card data-testid="quiz-details">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="text-purple-600" size={20} />
              Quiz Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                <span className="text-sm text-gray-600">Pass Rate</span>
                <span className="text-xl font-bold text-purple-600">{quizzes.pass_rate}%</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 bg-gray-50 rounded">
                  <p className="text-lg font-bold text-gray-900">{quizzes.total}</p>
                  <p className="text-xs text-gray-500">Total</p>
                </div>
                <div className="p-2 bg-blue-50 rounded">
                  <p className="text-lg font-bold text-blue-600">{quizzes.attempted}</p>
                  <p className="text-xs text-gray-500">Attempted</p>
                </div>
                <div className="p-2 bg-green-50 rounded">
                  <p className="text-lg font-bold text-green-600">{quizzes.passed}</p>
                  <p className="text-xs text-gray-500">Passed</p>
                </div>
              </div>
              {quizzes.details && quizzes.details.length > 0 && (
                <div className="mt-3 max-h-40 overflow-y-auto">
                  {quizzes.details.map((q, i) => (
                    <div key={i} className="flex justify-between items-center py-2 border-b last:border-0">
                      <span className="text-sm truncate max-w-[150px]">{q.title}</span>
                      <div className="flex items-center gap-2">
                        {q.passed && <CheckCircle className="text-green-500" size={14} />}
                        <span className={`text-sm font-medium ${q.passed ? 'text-green-600' : q.status === 'attempted' ? 'text-orange-500' : 'text-gray-400'}`}>
                          {q.best_percentage !== null ? `${q.best_percentage}%` : 'Not attempted'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Weekly Activity Chart */}
      <Card className="mb-6" data-testid="weekly-activity">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="text-indigo-600" size={20} />
            Weekly Activity (Last 4 Weeks)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-between items-end h-40 gap-2">
            {weekly_activity && weekly_activity.map((week, i) => {
              const maxActivity = Math.max(...weekly_activity.map(w => w.total_activity), 1);
              const height = (week.total_activity / maxActivity) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center">
                  <div className="w-full bg-gray-100 rounded-t relative" style={{ height: '120px' }}>
                    <div 
                      className="absolute bottom-0 w-full bg-gradient-to-t from-indigo-600 to-indigo-400 rounded-t transition-all duration-500"
                      style={{ height: `${Math.max(height, 5)}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">{week.week_start}</p>
                  <p className="text-xs font-medium text-indigo-600">{week.total_activity}</p>
                </div>
              );
            })}
          </div>
          <div className="flex justify-center gap-6 mt-4 text-xs">
            <span className="flex items-center gap-1"><div className="w-3 h-3 bg-blue-500 rounded" /> Content</span>
            <span className="flex items-center gap-1"><div className="w-3 h-3 bg-green-500 rounded" /> Assignments</span>
            <span className="flex items-center gap-1"><div className="w-3 h-3 bg-purple-500 rounded" /> Quizzes</span>
          </div>
        </CardContent>
      </Card>

      {/* Achievements */}
      <Card data-testid="achievements">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Award className="text-yellow-500" size={20} />
            Achievements
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className={`p-4 rounded-lg text-center ${achievements?.content_master ? 'bg-yellow-50 border-2 border-yellow-300' : 'bg-gray-50 opacity-50'}`}>
              <BookOpen className={achievements?.content_master ? 'text-yellow-500 mx-auto' : 'text-gray-400 mx-auto'} size={32} />
              <p className="text-sm font-medium mt-2">Content Master</p>
              <p className="text-xs text-gray-500">Complete 10+ content</p>
              {achievements?.content_master && <Star className="text-yellow-500 mx-auto mt-1" size={16} />}
            </div>
            <div className={`p-4 rounded-lg text-center ${achievements?.quiz_champion ? 'bg-purple-50 border-2 border-purple-300' : 'bg-gray-50 opacity-50'}`}>
              <Trophy className={achievements?.quiz_champion ? 'text-purple-500 mx-auto' : 'text-gray-400 mx-auto'} size={32} />
              <p className="text-sm font-medium mt-2">Quiz Champion</p>
              <p className="text-xs text-gray-500">Pass 3+ quizzes</p>
              {achievements?.quiz_champion && <Star className="text-purple-500 mx-auto mt-1" size={16} />}
            </div>
            <div className={`p-4 rounded-lg text-center ${achievements?.assignment_star ? 'bg-green-50 border-2 border-green-300' : 'bg-gray-50 opacity-50'}`}>
              <Medal className={achievements?.assignment_star ? 'text-green-500 mx-auto' : 'text-gray-400 mx-auto'} size={32} />
              <p className="text-sm font-medium mt-2">Assignment Star</p>
              <p className="text-xs text-gray-500">Get 5+ graded</p>
              {achievements?.assignment_star && <Star className="text-green-500 mx-auto mt-1" size={16} />}
            </div>
            <div className={`p-4 rounded-lg text-center ${achievements?.perfect_attendance ? 'bg-orange-50 border-2 border-orange-300' : 'bg-gray-50 opacity-50'}`}>
              <Crown className={achievements?.perfect_attendance ? 'text-orange-500 mx-auto' : 'text-gray-400 mx-auto'} size={32} />
              <p className="text-sm font-medium mt-2">Perfect Attendance</p>
              <p className="text-xs text-gray-500">Attend all classes</p>
              {achievements?.perfect_attendance && <Star className="text-orange-500 mx-auto mt-1" size={16} />}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
